import React, { useCallback, useEffect, useState } from "react";
import { Row } from "@tanstack/react-table";
import _ from "lodash";

export const TABLE_SELECT_COMPOSITE_KEY_SEPARATOR = "_____";
function makeCompositeKey<T>(key: (keyof T)[], r: T) {
  return key.map((k) => r[k]).join(TABLE_SELECT_COMPOSITE_KEY_SEPARATOR);
}

export function useTableSelect<T>(key: keyof T | (keyof T)[], contents: Array<T>) {
  const [selectedContents, setSelectedContents] = useState<any[]>([]);
  const [resetSelectedItems, setResetSelectedItems] = useState<
    ((value: Record<string, any>) => any) | null
  >(null);
  const [lastSelectedItem, setLastSelectedItem] = useState<
    string | number | null
  >(null);
  const [isShiftHold, setShiftHold] = useState<boolean>(false);

  useEffect(() => {
    window.onkeyup = (e) => {
      if (e.key === "Shift") {
        setShiftHold(false);
      }
    };

    document.addEventListener("keydown", (e) => {
      if (e.key === "Shift") {
        setShiftHold(true);
      }
    });
  }, [setShiftHold]);

  const handleRowSelect = useCallback(
    (
      items: Row<T>[],
      updateTableSelectionState: React.SetStateAction<any>,
      reset?: () => any,
      allItems?: Row<T>[]
    ) => {
      const selectedItemsIds = items.map((r) => {
        if (Array.isArray(key)) {
          return makeCompositeKey(key, r.original);
        }

        return r.original[key];
      });

      setSelectedContents(selectedItemsIds);

      if (reset != null) {
        setResetSelectedItems(() => reset);
      }

      if (selectedItemsIds.length > selectedContents.length) {
        const newSelectedItem = _.xor(selectedItemsIds, selectedContents)[0];
        setLastSelectedItem(newSelectedItem);
      } else {
        return;
      }

      if (selectedItemsIds.length <= 1 || !isShiftHold) {
        return;
      }

      // Проверяем, что добавили элемент, а не убрали
      if (selectedItemsIds.length > selectedContents.length) {
        const newSelectedItem = _.xor(selectedItemsIds, selectedContents)[0];
        setLastSelectedItem(newSelectedItem);
        if (lastSelectedItem != null) {
          let isStart = false;
          const x = contents
            .filter((c) => {
              const isInclude = [lastSelectedItem, newSelectedItem].includes(
                Array.isArray(key) ? makeCompositeKey(key, c) : c[key]
              );
              // Начало диапазона
              if (isInclude && !isStart) {
                isStart = true;
                return true;
              }
              // Конец диапазона
              else if (isInclude && isStart) {
                isStart = false;
                return true;
              }

              return isStart;
            })
            .map((c: T) => Array.isArray(key) ? makeCompositeKey(key, c) : c[key]);

          const rowState = {} as any;
          allItems
            ?.filter((c) => x.includes(Array.isArray(key) ? makeCompositeKey(key, c.original) : c.original[key]))
            .forEach((c) => {
              rowState[c.id] = true;
            });

          updateTableSelectionState((prevState: Record<any, boolean>) => ({
            ...prevState,
            ...rowState
          }));
          return;
        }
      } else {
        setLastSelectedItem(null);
        return;
      }

      let isStart = false;
      const multiSelectedItemsIds = contents
        .filter((c: T) => {
          const isInclude = [
            selectedItemsIds[0],
            selectedItemsIds[selectedItemsIds.length - 1]
          ].includes(Array.isArray(key) ? makeCompositeKey(key, c) : c[key]);
          // Начало диапазона
          if (isInclude && !isStart) {
            isStart = true;
            return true;
          }
          // Конец диапазона
          else if (isInclude && isStart) {
            isStart = false;
            return true;
          }

          return isStart;
        })
        .map((c: T) => Array.isArray(key) ? makeCompositeKey(key, c) : c[key]);

      setSelectedContents(multiSelectedItemsIds);
      const rowState = {} as Record<string, boolean>;
      allItems
        ?.filter((c) => multiSelectedItemsIds.includes(Array.isArray(key) ? makeCompositeKey(key, c.original) : c.original[key]))
        .forEach((c) => {
          rowState[c.id] = true;
        });

      updateTableSelectionState(rowState);
    },
    [isShiftHold, contents, selectedContents, lastSelectedItem]
  );

  return {
    selectedContents,
    handleRowSelect,
    resetSelectedItems
  };
}
